import {
  hashKey,
  keepPreviousData,
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type Query,
  type QueryClient,
  type UseMutationOptions,
} from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useSyncExternalStore } from "react";

import {
  specificationCreate,
  specificationDeleteVersion,
  specificationDistinctValues,
  specificationRename,
  specificationSetArchived,
  specificationSummary,
  specificationSummaryByVersion,
  specificationVersionLines,
  specificationsList,
} from "@/api/functions/specifications";
import type { CommunityDetails } from "@/hooks/communities";
import {
  communityDetailsQueryKey,
  communityDetailsQueryKeyPrefix,
} from "@/hooks/community-details-key";
import { SOURCE_CACHE_LIFETIME } from "@/hooks/source-cache-lifetime";
import { specLinesPagingOptions } from "@/hooks/spec-lines-paging";
import { markSpecHistoryStale } from "@/hooks/spec-history-keys";
import { markWhereToSaveStale } from "@/hooks/spec-over-target-keys";
import {
  LINKED_SPECS_PAGE_SIZE,
  SPEC_LIST_MAX_PAGE_SIZE,
  type SpecCreateBrowserInput,
  type SpecDeleteVersionBrowserInput,
  type SpecDistinctValuesBrowserInput,
  type SpecListQueryBrowserInput,
  type SpecRenameBrowserInput,
  type SpecSetArchivedBrowserInput,
  type SpecType,
} from "@/api/contracts/specifications";
import type { SpecLineStatusName } from "@/api/contracts/spec-editor";

// The Spec list's read model (#689; ADR-0014 decision 2): one query key per
// list request and one per distinct-values request, TanStack Query as the only
// cache — no normalized object store beside it.
//
// The list's options are what the page's behavior between two requests rests on:
//
//   - **`keepPreviousData`.** A page flip, a sort or a filter is a new key, and a
//     new key's default is `pending` with no data — which would blank the table.
//     The previous page's rows stay on screen instead, `isPlaceholderData` set,
//     so a page flip never blanks the table.
//   - **The source's cache lifetime, RTK Query's defaults** (`apiSlice.ts`; the
//     list's query overrides none). A request already answered is served from
//     the cache and not asked again while anything shows it, or for 60 seconds
//     after the last thing that showed it unmounts — so a return to the list
//     within a minute fires nothing and shows no "checking for updates"
//     spinner. Past that the entry is gone and the next visit loads afresh.
//     Nothing refetches on focus or on reconnect. What does refetch a shown
//     page is a mutation invalidating the prefix (the source's tag
//     invalidation), and that refetch is what the spinner reads, `isFetching`
//     with data present.
//
// The Linked Specs popover is the exception the page ticket (#691) owns: the
// source refetches it on every open (`forceRefetch: () => true`,
// `linked-specs/actions.ts`), so it takes these options with `staleTime: 0`
// and `refetchOnMount: "always"` laid over them.
//
// The key carries the whole input: every field changes which rows come back,
// so a key that dropped one would serve a cached page from another request.
// That is also what lets the lifecycle ticket (#696) write a mutation's
// corrected whole page into exactly the key of the query it names, then
// invalidate the rest under the prefix (ADR-0014 decisions 1 and 4).

/**
 * What every Spec list key starts with, and what a lifecycle mutation
 * invalidates around. Exported as a value so a second spelling cannot quietly
 * stop matching the keys it is meant to reach.
 */
export const specificationsListQueryKeyPrefix = ["specifications", "list"] as const;

export const specificationsListQueryKey = (input: SpecListQueryBrowserInput) =>
  [...specificationsListQueryKeyPrefix, input] as const;

export const specificationsListQueryOptions = (input: SpecListQueryBrowserInput) =>
  queryOptions({
    queryKey: specificationsListQueryKey(input),
    queryFn: () => specificationsList({ data: input }),
    placeholderData: keepPreviousData,
    ...SOURCE_CACHE_LIFETIME,
  });

/**
 * `enabled` holds the read back until the page knows its full input — the
 * Spec list's remembered division, read from the browser after mount — rather
 * than sending an unscoped request it would throw away a frame later.
 */
export function useSpecificationsList(
  input: SpecListQueryBrowserInput,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery({ ...specificationsListQueryOptions(input), enabled });
}

/**
 * One page of the Spec list as the page renders it, and one row of it — the
 * server function's own answer, so the client graph names the shape without
 * importing the wire face (`client/docs/architecture.md`, "The dependency
 * rule").
 */
export type SpecListPage = Awaited<ReturnType<typeof specificationsList>>;
export type SpecListRow = SpecListPage["rows"][number];

/**
 * The Linked Specs popover's read (#691): the list read under a parent — the
 * child tab, active children only, one page of 100, no sort and no division
 * (`linked-specs/actions.ts:56-65`) — with the list's options and the source's
 * `forceRefetch` laid over them: `staleTime: 0` and `refetchOnMount:
 * "always"`, so every open asks again and shows its skeletons. No placeholder:
 * a popover has one parent, and another parent's rows are never a stand-in.
 */
export const linkedSpecsQueryOptions = (parentSpecId: number, childType: SpecType) => ({
  ...specificationsListQueryOptions({
    specType: childType,
    status: "active",
    page: 1,
    pageSize: LINKED_SPECS_PAGE_SIZE,
    parentSpecId,
  }),
  placeholderData: undefined,
  staleTime: 0,
  refetchOnMount: "always" as const,
});

/** The popover's hook; `enabled` is the popover's open state. */
export function useLinkedSpecs(parentSpecId: number, childType: SpecType, enabled: boolean) {
  return useQuery({ ...linkedSpecsQueryOptions(parentSpecId, childType), enabled });
}

/**
 * The Create Visualization dialog's candidate pool (#755): **every** active
 * community spec of one community, newest created first — the server's default
 * sort, TIM's `-created_at`. The donor asked for one page of ten
 * (`CommunityDetailsIsland.tsx:97-110`), so an older published spec behind ten
 * newer ones was never offered (#724 Q12, a named parity break): this walks the
 * list read at its largest page until it holds `total` rows, stopping early on
 * an empty page — a spec archived mid-walk shrinks the tab under it.
 *
 * One query, separate from the page's spec table (which carries the user's
 * sort, filters and page), keyed under the list prefix so a lifecycle write's
 * invalidation refreshes it too. A page that fails fails the pool: a partial
 * pool could hide the one published spec.
 */
export const communitySpecPoolQueryKey = (internalCommunityId: number) =>
  [...specificationsListQueryKeyPrefix, "community-pool", internalCommunityId] as const;

export const communitySpecPoolQueryOptions = (internalCommunityId: number) =>
  queryOptions({
    queryKey: communitySpecPoolQueryKey(internalCommunityId),
    queryFn: async () => {
      const rows: SpecListRow[] = [];
      for (let page = 1; ; page += 1) {
        const answer = await specificationsList({
          data: {
            specType: "community",
            status: "active",
            internalCommunityId,
            page,
            pageSize: SPEC_LIST_MAX_PAGE_SIZE,
          },
        });
        rows.push(...answer.rows);
        if (answer.rows.length === 0 || rows.length >= answer.total) return rows;
      }
    },
    ...SOURCE_CACHE_LIFETIME,
  });

export function useCommunitySpecPool(internalCommunityId: number) {
  return useQuery(communitySpecPoolQueryOptions(internalCommunityId));
}

/**
 * The distinct values a column filter's popover offers. Its own key per
 * request — tab, column, division, search term and limit — and the same
 * cache lifetime as the list: a popover reopened within a minute is served
 * from the cache, as the source's is.
 */
export const specificationDistinctValuesQueryKeyPrefix = [
  "specifications",
  "distinct-values",
] as const;

export const specificationDistinctValuesQueryKey = (input: SpecDistinctValuesBrowserInput) =>
  [...specificationDistinctValuesQueryKeyPrefix, input] as const;

export const specificationDistinctValuesQueryOptions = (input: SpecDistinctValuesBrowserInput) =>
  queryOptions({
    queryKey: specificationDistinctValuesQueryKey(input),
    queryFn: () => specificationDistinctValues({ data: input }),
    placeholderData: keepPreviousData,
    ...SOURCE_CACHE_LIFETIME,
  });

export function useSpecificationDistinctValues(input: SpecDistinctValuesBrowserInput) {
  return useQuery(specificationDistinctValuesQueryOptions(input));
}

// ---------------------------------------------------------------------------
// The details page (#693; ADR-0014 decision 2): two read models
// ---------------------------------------------------------------------------
//
// A **spec summary** per spec, which the Spec header, the Version dropdown, the
// banners, the breadcrumb, the metadata line and the page title all read; and
// a **lines page** infinite query per (spec, version, term), which the lines
// body mounts and the Editor inherits. The Editor's mutation hooks write their
// corrected read models into these keys and invalidate the rest under the
// exported prefixes (ADR-0014 decisions 2 and 3), so each prefix is a value.
//
// Both answer `null` for a declared absence — a dead or foreign id — which is
// what the route renders Page not found (or the base list) from.

/** What every spec summary key starts with. */
export const specificationSummaryQueryKeyPrefix = ["specifications", "summary"] as const;

export const specificationSummaryQueryKey = (specificationId: number) =>
  [...specificationSummaryQueryKeyPrefix, specificationId] as const;

export const specificationSummaryQueryOptions = (specificationId: number) =>
  queryOptions({
    queryKey: specificationSummaryQueryKey(specificationId),
    queryFn: () => specificationSummary({ data: { specificationId } }),
    ...SOURCE_CACHE_LIFETIME,
  });

/**
 * `null` is a URL segment that is not an id: nothing is read, as the route
 * answers it before any read (Page not found, or the base list) — the spec
 * layout renders over its child while the child's loader decides.
 *
 * `retryOnMount: false` is the details page's: after its loader's read has
 * failed, the donor asks once, and its snackbar says to refresh (#554 D4 (i)).
 */
export function useSpecificationSummary(
  specificationId: number | null,
  { retryOnMount = true }: { retryOnMount?: boolean } = {},
) {
  return useQuery({
    ...specificationSummaryQueryOptions(specificationId ?? 0),
    enabled: specificationId !== null,
    retryOnMount,
  });
}

export const specificationSummaryByVersionQueryKey = (versionId: number) =>
  [...specificationSummaryQueryKeyPrefix, "by-version", versionId] as const;

/**
 * The `/specs/:versionId/*` resolver's read: the summary of the spec a version
 * belongs to. Its own key — the spec id is what it discovers — and on success it
 * **seeds the by-spec key** with the same answer, so the details page the
 * resolver redirects to renders from a warm cache instead of asking again.
 * The client is a parameter because the seed is a write into a key this query
 * does not own.
 */
export const specificationSummaryByVersionQueryOptions = (
  queryClient: QueryClient,
  versionId: number,
) =>
  queryOptions({
    queryKey: specificationSummaryByVersionQueryKey(versionId),
    queryFn: async () => {
      const summary = await specificationSummaryByVersion({ data: { versionId } });
      if (summary !== null) {
        queryClient.setQueryData(specificationSummaryQueryKey(summary.id), summary);
      }
      return summary;
    },
    ...SOURCE_CACHE_LIFETIME,
  });

/** What every lines-page key starts with. */
export const specificationLinesQueryKeyPrefix = ["specifications", "lines"] as const;

/** One infinite query per spec, version and term; no term is `""`. */
export const specificationLinesQueryKey = (
  specificationId: number,
  versionId: number,
  search = "",
) => [...specificationLinesQueryKeyPrefix, specificationId, versionId, search] as const;

/**
 * The Review & Publish dialog's step 1 (#950): one infinite query per spec,
 * version and the statuses it lists. Under the version's lines prefix, so an
 * Editor result that does not correct it marks it stale with every other
 * scope; its last part is an object, never a string, so no term's key is
 * ever it.
 */
export const specificationReviewLinesQueryKey = (
  specificationId: number,
  versionId: number,
  statuses: readonly SpecLineStatusName[],
) =>
  [
    ...specificationLinesQueryKeyPrefix,
    specificationId,
    versionId,
    { review: [...statuses] },
  ] as const;

/**
 * A count of the writes to one version's cached lines pages — every Editor
 * write (a guess, a result, a rollback: `setQueryData`'s `manual` success) and
 * every page the infinite scroll loads, but **never a refetch**: a visible
 * change caused by a background refetch is a defect (ADR-0014 decision 1).
 *
 * It stands for the donor's one object cache, whose every write hands each
 * subscriber fresh arrays: the per-location notes table re-opens on it (#808).
 * The version is read when a write lands, as the Editor's target is.
 */
export function useSpecLinesWriteStamp(
  version: () => { specificationId: number; versionId: number },
) {
  const queryClient = useQueryClient();
  const cache = queryClient.getQueryCache();
  const stamp = useRef(0);
  // The latest getter, read when a write lands: an inline getter, new every
  // render, never re-subscribes the listener.
  const versionRef = useRef(version);
  useEffect(() => {
    versionRef.current = version;
  });
  const subscribe = useCallback(
    (onChange: () => void) =>
      cache.subscribe((event) => {
        if (event.type !== "updated" || event.action.type !== "success") return;
        const { specificationId, versionId } = versionRef.current();
        const prefix = [...specificationLinesQueryKeyPrefix, specificationId, versionId];
        if (!prefix.every((part, index) => event.query.queryKey[index] === part)) return;
        if (!event.action.manual && !event.query.state.fetchMeta?.fetchMore) return;
        stamp.current += 1;
        onChange();
      }),
    [cache],
  );
  const read = () => stamp.current;
  // The server renders no write: the same 0 the client starts from.
  return useSyncExternalStore(subscribe, read, read);
}

/**
 * A later lines page's request (#910): the cursor the last page answered
 * with, every line the view's pages hold — the server leaves them out, as the
 * source's `loadLines` dedupe did — and under a term the last page's groups,
 * the search's snapshot, the only groups the page draws lines into.
 */
export interface SpecLinesNextPage {
  cursor: number;
  heldLineIds: number[];
  snapshotGroupIds?: number[];
}

/** A plain cursor for the first page (fixtures seed `pageParams: [0]`), else
 *  a later page's request. */
export type SpecLinesPageParam = number | SpecLinesNextPage;

/**
 * A version's lines, 25 at a time on infinite scroll: the first page at offset
 * 0, each next one at the cursor the last page answered with, none once it says
 * there is no more — or once the answer is `null`, a version not found. The
 * term is sent only when there is one, as the source drops an empty one.
 *
 * **Each next page's request comes from the pages it follows**, through
 * TanStack's next-page parameter, at the moment it is asked for — never read
 * from the cache inside the fetch. So a page after an Editor result sends what
 * the result left on screen, and a re-read, which derives each later page's
 * parameter from the pages it has just re-read (TanStack 5.104.1's
 * `infiniteQueryBehavior`), rebuilds a consistent fresh view rather than
 * skipping every row the stale pages held.
 */
export const specificationLinesQueryOptions = (
  specificationId: number,
  versionId: number,
  search = "",
) =>
  specLinesPagingOptions(
    (term) => specificationLinesQueryKey(specificationId, versionId, term),
    search,
    (request) => specificationVersionLines({ data: { specificationId, versionId, ...request } }),
  );

export function useSpecificationLines(specificationId: number, versionId: number, search = "") {
  return useInfiniteQuery(specificationLinesQueryOptions(specificationId, versionId, search));
}

// ---------------------------------------------------------------------------
// The lifecycle verbs (#707; ADR-0014 decisions 1, 4 and 5)
// ---------------------------------------------------------------------------
//
// Rename, set archived and delete version each name the list query the page is
// showing and answer with that page recomputed after the write. The success
// handler writes it straight into that query's key, so the row leaves, moves or
// updates in the same frame — no loader, no second visible change — and then
// marks everything else the write made stale: every other list entry, and the
// spec's summary and lines reads. **No `onMutate`**: the list takes no
// optimistic change, as the source's list actions took none (decision 5). A
// refused write changes nothing on the server, so nothing is touched on
// failure either.
//
// `useMutation`'s `variables.specId` is the pending spec, which is what #696's
// row spinner reads.
//
// Every invalidation below is started, not awaited: the dialog closes on
// success, and holding the settle behind refetches of reads nobody is looking
// at would leave its button spinning after the write landed.

export type SpecListPageResult = Awaited<ReturnType<typeof specificationRename>>;

/** Whether a cached summary is this spec's — by spec id, or by version id
 *  once its answer has named the spec. */
function isSummaryOf(query: Query, specId: number): boolean {
  const key = query.queryKey;
  if (hashKey(key) === hashKey(specificationSummaryQueryKey(specId))) return true;
  const data = query.state.data as { id: number } | null | undefined;
  return (
    key.length === specificationSummaryQueryKeyPrefix.length + 2 &&
    key[specificationSummaryQueryKeyPrefix.length] === "by-version" &&
    data?.id === specId
  );
}

const summaryFilter = (specId: number) => ({
  queryKey: specificationSummaryQueryKeyPrefix,
  predicate: (query: Query) => isSummaryOf(query, specId),
});

const linesFilter = (specId: number) => ({
  queryKey: [...specificationLinesQueryKeyPrefix, specId],
});

/** Write the corrected page into the key the request named; mark every other
 *  list entry stale. */
async function writeCorrectedPage(
  queryClient: QueryClient,
  listQuery: SpecListQueryBrowserInput,
  page: SpecListPageResult,
): Promise<void> {
  const key = specificationsListQueryKey(listQuery);
  // A read of this key already on the wire would land after the write below
  // and put the pre-write page back; cancelling first is React Query's answer.
  await queryClient.cancelQueries({ queryKey: key, exact: true });
  queryClient.setQueryData(key, page);
  void queryClient.invalidateQueries({
    queryKey: specificationsListQueryKeyPrefix,
    predicate: (query) => hashKey(query.queryKey) !== hashKey(key),
  });
}

/** A rename's or an archive flip's success: the corrected page, then the
 *  spec's own reads marked stale — its header, dropdown and edit guard read
 *  the name and the archive flags. */
async function correctAfterWrite(
  queryClient: QueryClient,
  specId: number,
  listQuery: SpecListQueryBrowserInput,
  page: SpecListPageResult,
): Promise<void> {
  await writeCorrectedPage(queryClient, listQuery, page);
  markSpecReadsStale(queryClient, specId);
}

/** The spec's own reads — its header summaries, its lines and its history —
 *  marked stale. */
export function markSpecReadsStale(queryClient: QueryClient, specId: number): void {
  void queryClient.invalidateQueries(summaryFilter(specId));
  void queryClient.invalidateQueries(linesFilter(specId));
  markSpecHistoryStale(queryClient, specId);
}

/**
 * Create from scratch. Answers with the new spec's and draft's ids, which the
 * dialog navigates to; the list is never seen again on that path, so its
 * entries are marked stale and **not refetched** (`refetchType: "none"`) — the
 * way back reads afresh, and the navigation is not held behind a list read.
 */
export function createSpecificationMutationOptions(
  queryClient: QueryClient,
): UseMutationOptions<
  Awaited<ReturnType<typeof specificationCreate>>,
  Error,
  SpecCreateBrowserInput
> {
  return {
    mutationFn: (data) => specificationCreate({ data }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: specificationsListQueryKeyPrefix,
        refetchType: "none",
      });
    },
  };
}

export function useCreateSpecification() {
  const queryClient = useQueryClient();
  return useMutation(createSpecificationMutationOptions(queryClient));
}

/**
 * A rename that moved a spec out of the community its list names — owner D1
 * (#751, 2026-10-07): the community details page's Specifications tile drops by
 * one at once, as modsy's did. Modsy's Edit Name & ID answer carried both
 * communities' partials when the Community ID changed (Falcon
 * `specs_list_island/handlers/actions.py:138-155`) and its observer merged them
 * into the cache (`CommunityObserversIsland`), so the tile moved with no
 * refetch.
 *
 * The moved spec was one of the community's active specs — the table lists
 * only those — so its count included it: the list's community (its internal
 * id, the table's scope) is lowered by one when its cached details name
 * another composite id than the new one, keeping its fetch time (a patch is
 * not a newly fetched answer, so the page's feed snackbar does not fire again).
 * Every cached entry for the new id is **marked stale without refetching**:
 * the next visit reads it afresh — even with no cached details for the list's
 * community, when nothing can be lowered. No details refetch and no feed call.
 * The ids compare exactly, as the rename verb stores the typed id as given
 * (`rename_spec.py`). Create, duplicate, archive and delete emit nothing, in
 * both apps.
 */
function followCommunityMove(queryClient: QueryClient, input: SpecRenameBrowserInput): void {
  followCommunityMoveFrom(queryClient, input.listQuery.internalCommunityId, input.communityId);
}

/** The move itself, from the community `listed` (its internal id) names to the
 *  composite id `to` — the list rename's, and the Editor header's
 *  (`@/hooks/spec-editor-summary`, #799). */
export function followCommunityMoveFrom(
  queryClient: QueryClient,
  listed: number | undefined,
  to: string | undefined,
): void {
  if (listed === undefined || to === undefined) return;
  const fromKey = communityDetailsQueryKey(listed);
  const fromQuery = queryClient.getQueryCache().find({ queryKey: fromKey, exact: true });
  const from = fromQuery?.state.data as CommunityDetails | undefined;
  if (from?.externalCompositeId === to) return;
  if (fromQuery && from) {
    queryClient.setQueryData<NonNullable<CommunityDetails>>(
      fromKey,
      { ...from, specificationsCount: Math.max(0, from.specificationsCount - 1) },
      { updatedAt: fromQuery.state.dataUpdatedAt },
    );
  }
  for (const query of queryClient.getQueryCache().findAll({
    queryKey: communityDetailsQueryKeyPrefix,
  })) {
    const community = query.state.data as CommunityDetails | undefined;
    if (query === fromQuery || community?.externalCompositeId !== to) continue;
    void queryClient.invalidateQueries({
      queryKey: query.queryKey,
      exact: true,
      refetchType: "none",
    });
  }
}

/** Rename — the row updates (or moves, under a name sort) in place. */
export function renameSpecificationMutationOptions(
  queryClient: QueryClient,
): UseMutationOptions<SpecListPageResult, Error, SpecRenameBrowserInput> {
  return {
    mutationFn: (data) => specificationRename({ data }),
    onSuccess: async (page, input) => {
      followCommunityMove(queryClient, input);
      await correctAfterWrite(queryClient, input.specId, input.listQuery, page);
    },
  };
}

export function useRenameSpecification() {
  const queryClient = useQueryClient();
  return useMutation(renameSpecificationMutationOptions(queryClient));
}

/** Archive or Enable — the row leaves the tab's Active or Archived page. */
export function setSpecificationArchivedMutationOptions(
  queryClient: QueryClient,
): UseMutationOptions<SpecListPageResult, Error, SpecSetArchivedBrowserInput> {
  return {
    mutationFn: (data) => specificationSetArchived({ data }),
    onSuccess: (page, { specId, listQuery }) =>
      correctAfterWrite(queryClient, specId, listQuery, page),
  };
}

export function useSetSpecificationArchived() {
  const queryClient = useQueryClient();
  return useMutation(setSpecificationArchivedMutationOptions(queryClient));
}

/**
 * Whether a delete took the spec row with it (ADR-0015): a Shell's delete
 * always does, and a version's does when it was the spec's only one — which
 * any cached summary of the spec, by spec or by version, says: it is the one
 * read that lists the versions. With none cached there is nothing of the
 * spec's summary to remove, and its lines entries are only ever cached beside
 * one.
 */
function deleteTookTheSpec(
  queryClient: QueryClient,
  specId: number,
  versionId: number | null,
): boolean {
  if (versionId === null) return true;
  const summary = queryClient
    .getQueryCache()
    .findAll(summaryFilter(specId))
    .map((query) => query.state.data as { versions: { id: number }[] } | null | undefined)
    .find((data) => data != null);
  return summary?.versions.length === 1 && summary.versions[0]!.id === versionId;
}

/**
 * Delete a version (or a Shell's own row) — the row leaves the page. A spec the
 * delete removed has its summary and lines entries **removed**, not
 * invalidated: there is nothing left to re-read. Otherwise the deleted
 * version's lines go and the rest of the spec's reads are marked stale.
 */
export function deleteSpecificationVersionMutationOptions(
  queryClient: QueryClient,
): UseMutationOptions<SpecListPageResult, Error, SpecDeleteVersionBrowserInput> {
  return {
    mutationFn: (data) => specificationDeleteVersion({ data }),
    onSuccess: async (page, { specId, versionId, listQuery }) => {
      const specGone = deleteTookTheSpec(queryClient, specId, versionId);
      await writeCorrectedPage(queryClient, listQuery, page);
      // The spec list's History gains the delete.
      markSpecHistoryStale(queryClient, specId);
      if (specGone) {
        queryClient.removeQueries(summaryFilter(specId));
        queryClient.removeQueries(linesFilter(specId));
        return;
      }
      queryClient.removeQueries({
        queryKey: [...specificationLinesQueryKeyPrefix, specId, versionId],
      });
      void queryClient.invalidateQueries(summaryFilter(specId));
      void queryClient.invalidateQueries(linesFilter(specId));
      // The rows are the Latest version's, which a delete can change (#1243 D2).
      void markWhereToSaveStale(queryClient, specId);
    },
  };
}

export function useDeleteSpecificationVersion() {
  const queryClient = useQueryClient();
  return useMutation(deleteSpecificationVersionMutationOptions(queryClient));
}

/**
 * One lines page as the body renders it, and its parts — the server function's
 * own answer, as `SpecListPage` above is.
 */
export type SpecLinesPage = NonNullable<Awaited<ReturnType<typeof specificationVersionLines>>>;
export type SpecLinesGroup = SpecLinesPage["groups"][number];
export type SpecLinesLine = SpecLinesPage["lines"][number];
export type SpecLinesSelection = SpecLinesLine["selections"][number];
export type SpecLinesLocation = SpecLinesPage["locations"][number];
